import ActionIconButton from '@components/Common/ActionIconButton';
import EmptyState from '@components/Common/EmptyState';
import { SectionCard } from '@components/Common/EntityWorkspace';
import FilterRow from '@components/Common/FilterRow';
import FormField from '@components/Common/FormField';
import MetricTable, { MetricColumn } from '@components/Common/MetricTable';
import { Panel } from '@components/Common/Panel';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import StatTile, { StatTone } from '@components/Common/StatTile';
import LinkOffOutlinedIcon from '@mui/icons-material/LinkOffOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Box, Button, Chip } from '@mui/material';
import axios from 'axios';
import React, { useCallback, useEffect, useState } from 'react';
import { currentMonth, DailyRow, dayLabel, isConfigError, isProblem, LineItem, ProviderProblem, usd } from './costData';

/*
 * The pieces every provider tab is built from, so the four tabs are one page with one skeleton:
 * toolbar, tiles, then titled cards. They were four hand-built layouts, and it showed — a red
 * "Total spend" on one tab, an uppercase label floating outside a card on another, a yellow alert
 * where a third tab had its numbers.
 */

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------

export interface CostQuery<T> {
  month: string;
  setMonth: (m: string) => void;
  data: T | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

/**
 * One month of one provider's costs. `buildUrl` turns the month into that provider's query, since
 * AWS and OpenAI take a date range and Atlas takes the month itself.
 */
export function useCostQuery<T>(buildUrl: (month: string) => string, fallbackError: string): CostQuery<T> {
  const [month, setMonth] = useState(currentMonth());
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(buildUrl(month));
      setData(res.data);
    } catch (e: any) {
      setData(null);
      setError(e?.response?.data?.error ?? fallbackError);
    } finally {
      setLoading(false);
    }
    // buildUrl is a module-level function per provider; month is what changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, fallbackError]);

  /*
   * Depends on refresh, so changing the month refetches. This was once `[]` with the lint rule
   * silenced, which made the month picker inert in the most misleading way a spend screen can be:
   * the subtitle followed the picker while the numbers stayed on the old month.
   */
  useEffect(() => {
    refresh();
  }, [refresh]);

  return { month, setMonth, data, loading, error, refresh };
}

// ---------------------------------------------------------------------------
// Toolbar
// ---------------------------------------------------------------------------

export const CostToolbar: React.FC<{ query: CostQuery<unknown> }> = ({ query }) => (
  <FilterRow className="ac-toolbar">
    <FormField
      required={false}
      name="api-costs-month"
      label="Month"
      type="month"
      value={query.month}
      textFieldProps={{ slotProps: { htmlInput: { max: currentMonth() } } }}
      onChange={(_n, v) => query.setMonth(String(v))}
    />
    <ActionIconButton label="Refresh" icon={<RefreshIcon />} busy={query.loading} onClick={query.refresh} />
  </FilterRow>
);

// ---------------------------------------------------------------------------
// Tiles
// ---------------------------------------------------------------------------

export interface CostTile {
  label: string;
  value: string;
  icon: React.ReactNode;
  sublabel?: string;
  /* Neutral unless the number is a state: only the budget tile ever sets this. */
  tone?: StatTone;
}

export const CostTiles: React.FC<{ tiles: CostTile[] }> = ({ tiles }) => (
  <Box className="cc-stat-grid">
    {tiles.map((t) => (
      <StatTile key={t.label} label={t.label} value={t.value} icon={t.icon} sublabel={t.sublabel} tone={t.tone ?? 'neutral'} />
    ))}
  </Box>
);

// ---------------------------------------------------------------------------
// Breakdown cards
// ---------------------------------------------------------------------------

interface CostSectionProps<T> {
  title: string;
  icon: React.ReactNode;
  meta?: string;
  columns: MetricColumn<T>[];
  rows: T[];
  getRowKey: (row: T, i: number) => string;
  emptyLabel?: string;
  footnote?: React.ReactNode;
}

/** A titled card holding one breakdown table. Same card on every tab. */
export function CostSection<T>({ title, icon, meta, columns, rows, getRowKey, emptyLabel, footnote }: CostSectionProps<T>) {
  return (
    <SectionCard title={title} icon={icon} meta={meta}>
      <div className="ac-section-stack">
        <MetricTable<T> columns={columns} rows={rows} getRowKey={getRowKey} emptyLabel={emptyLabel ?? 'No spend this month'} />
        {footnote && <p className="ac-footnote">{footnote}</p>}
      </div>
    </SectionCard>
  );
}

/**
 * A money cell. Neutral and bold rather than MetricTable's `strong` variant, which is green: a
 * cost is not good news, and colour on this page is kept for budget state alone.
 */
export const Amount: React.FC<{ cents: number }> = ({ cents }) => <span className="ac-amount">{usd(cents)}</span>;

/** Line items as chips, largest first: "Amazon S3 $1.20". */
export const LineItemChips: React.FC<{ items: LineItem[] }> = ({ items }) => (
  <span className="ac-chips">
    {items.map((li, i) => (
      <Chip key={`${li.name}-${i}`} size="small" label={`${li.name} ${usd(li.cost)}`} />
    ))}
  </span>
);

/** The daily table, identical for every metered provider; only the item noun changes. */
export function dailyColumns(itemNoun: string): MetricColumn<DailyRow>[] {
  return [
    { key: 'date', header: 'Date', variant: 'name', render: (r) => dayLabel(r.date) },
    { key: 'cost', header: 'Total', align: 'right', render: (r) => <Amount cents={r.cost} /> },
    { key: 'items', header: `By ${itemNoun}`, align: 'left', render: (r) => <LineItemChips items={r.items} /> },
  ];
}

// ---------------------------------------------------------------------------
// States
// ---------------------------------------------------------------------------

export interface ConnectCopy {
  /** What to do, in plain words. */
  body: string;
}

/**
 * The provider's billing feed is not set up. Said in words an admin can act on; the server's own
 * message, which names the exact settings, stays underneath as the detail whoever sets it up needs.
 */
export const CostNotConnected: React.FC<{ provider: string; copy: ConnectCopy; detail: string }> = ({
  provider,
  copy,
  detail,
}) => (
  <Panel>
    <EmptyState
      icon={<LinkOffOutlinedIcon />}
      title={`${provider} billing is not connected`}
      body={
        <>
          {copy.body}
          <span className="ac-config-detail">{detail}</span>
        </>
      }
    />
  </Panel>
);

const PROBLEM_TITLE: Record<ProviderProblem['state'], (p: string) => string> = {
  not_configured: (p) => `${p} billing is not connected`,
  access_denied: (p) => `${p} cost access not granted`,
  error: (p) => `${p} costs could not be loaded`,
};

/**
 * A provider the server cannot read, said as what to do: the server's sentence, the steps in
 * order, and the exact settings that are missing. Never an error banner and never a $0.
 */
export const CostSetupState: React.FC<{ provider: string; problem: ProviderProblem; onRetry?: () => void }> = ({
  provider,
  problem,
  onRetry,
}) => (
  <Panel>
    <EmptyState
      icon={problem.state === 'access_denied' ? <LockOutlinedIcon /> : <LinkOffOutlinedIcon />}
      title={PROBLEM_TITLE[problem.state]?.(provider) ?? PROBLEM_TITLE.error(provider)}
      body={
        <span className="ac-setup">
          <span className="ac-setup-message">{problem.message}</span>
          {!!problem.steps?.length && (
            <ol className="ac-setup-steps">
              {problem.steps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
          )}
          {!!problem.missing?.length && (
            <span className="ac-setup-missing">
              <span>Not set on the server:</span>
              {problem.missing.map((m) => (
                <code key={m} className="ac-config-var">
                  {m}
                </code>
              ))}
            </span>
          )}
        </span>
      }
      actions={
        problem.state === 'error' && onRetry ? (
          <Button variant="outlined" onClick={onRetry}>
            Try again
          </Button>
        ) : undefined
      }
    />
  </Panel>
);

/**
 * Loading, not-connected and failed, in that order; `null` means there is data to draw.
 * Every metered tab goes through this, so all three states look the same on all of them.
 */
export function costStatus(query: CostQuery<unknown>, provider: string, copy: ConnectCopy): React.ReactNode | null {
  if (query.loading && !query.data) return <SkeletonLoader rows={8} columns={4} />;
  if (isProblem(query.data)) return <CostSetupState provider={provider} problem={query.data} onRetry={query.refresh} />;
  if (query.error && isConfigError(query.error)) {
    return <CostNotConnected provider={provider} copy={copy} detail={query.error} />;
  }
  if (query.error) {
    return (
      <Panel>
        <EmptyState
          title={`${provider} costs could not be loaded`}
          body="The billing service did not answer. Try again in a moment."
          actions={
            <Button variant="outlined" onClick={query.refresh}>
              Try again
            </Button>
          }
        />
      </Panel>
    );
  }
  if (!query.data) return <SkeletonLoader rows={8} columns={4} />;
  return null;
}
